import type { ReactNode } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Pencil, Repeat } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { StarOrderButton } from "@/components/orders/StarOrderButton";
import { InlineEditCell } from "@/components/orders/InlineEditCell";
import { InlineTrailerTypeCell } from "@/components/orders/InlineTrailerTypeCell";
import { InlineAssignCell } from "@/components/orders/InlineAssignCell";
import { BolViewerDialog } from "@/components/orders/BolViewerDialog";
import { TenderViewerDialog } from "@/components/orders/TenderViewerDialog";
import { ReadyTimeDisplay, DeliveryTimeDisplay } from "@/components/orders/OrderTimeDisplay";
import { cn } from "@/lib/utils";
import {
  isLateDeparture,
  isLateArrival,
} from "@/lib/order-time";
import type { Order, Customer, Tractor, Driver, Location } from "@/types/tms";
import { useUserRole } from "@/hooks/useUserRole";
import { resolveOrderEndpoints } from "@/lib/order-endpoints";
import { OrderLoadStatusCell } from "@/components/orders/OrderLoadStatusCell";
import type { HistoricalLoadStatus } from "@/lib/order-load-status";
import { TrailerArrivedBadge } from "./TrailerArrivedBadge";
import type { LegWithArrivedTrailer } from "@/lib/order-trailer-arrival";

export type OrderRowOrder = Order & {
  billing_party: Customer | null;
  assigned_tractor: Tractor | null;
  assigned_operator: Driver | null;
};

export interface OrderRowProps {
  order: OrderRowOrder;
  onEdit?: (order: Order) => void;
  isChild?: boolean;
  legNumber?: number;
  totalLegs?: number;
  parentUniqueId?: string;
  onRowClick?: (order: OrderRowOrder) => void;
  isSelected?: boolean;
  /** When true, the leading checkbox cell is rendered. */
  showCheckbox?: boolean;
  isChecked?: boolean;
  onToggle?: (id: string) => void;
  /** When true, render a Load Status cell (between Trailer Type and Tender ID). */
  showLoadStatus?: boolean;
  loadStatus?: string | null;
  /** This leg row's trailer + leg status, for the "Trailer arrived · pending check-in" line. */
  legWithArrivedTrailer?: LegWithArrivedTrailer | null;
  /** At-delivery load status snapshot for this row's order, when available. */
  loadStatusHistorical?: HistoricalLoadStatus | null;
  locations?: Location[];
  /** Leg statuses for the rollup chip when the caller loads them separately
   *  (the orders list attaches them to the order itself). */
  legs?: Order["legs"];
  /** Custom contents for the trailing Actions cell. Defaults to BOL + Tender + Edit. */
  actionsSlot?: ReactNode;
  /** Human-readable trailer type label to display in the Trailer Type cell
   * for child leg rows (which render read-only, non-inline-editable values). */
  childTrailerTypeLabel?: string | null;
}

/**
 * Single shared order row used by the Orders page (`OrderListView`) and any
 * dashboard table that shows a subset of orders (e.g. Subscribed orders).
 *
 * Keeping a single source of truth ensures consistent timezone-aware time
 * formatting and consistent late-state styling across the app.
 */
export function OrderTableRow({
  order,
  onEdit,
  isChild = false,
  legNumber,
  totalLegs,
  parentUniqueId,
  onRowClick,
  isSelected,
  showCheckbox = true,
  isChecked,
  onToggle,
  showLoadStatus = true,
  loadStatus,
  legWithArrivedTrailer,
  loadStatusHistorical,
  locations,
  actionsSlot,
  childTrailerTypeLabel,
  legs,
}: OrderRowProps) {
  const lateDep = !isChild && isLateDeparture(order);
  const lateArr = !isChild && isLateArrival(order);
  const { isReadOnly } = useUserRole();
  const isTerminalStatus = order.status === "in_transit" || order.status === "delivered" || order.status === "canceled";
  const isTerminal = isTerminalStatus || isReadOnly;

  // Canonical short_code resolution shared by every order surface.
  // See src/lib/order-endpoints.ts for the contract.
  const { from: fromEndpoint, to: toEndpoint } = resolveOrderEndpoints(order, locations);
  const fromLabel = fromEndpoint.label;
  const toLabel = toEndpoint.label;

  const defaultActions = (
    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
      <BolViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
      <TenderViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
      {onEdit && !isReadOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={() => onEdit(order)}>
              <Pencil className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit</TooltipContent>
        </Tooltip>
      )}
    </div>
  );

  return (
    <TableRow
      selected={isSelected}
      className={cn(
        isChild ? "bg-muted [&>td:first-child]:bg-muted [&>td:nth-child(2)]:bg-muted" : "",
      )}
      onClick={() => onRowClick?.(order)}
    >
      {showCheckbox && !isChild && (
        <TableCell className="w-[40px]" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => onToggle?.(order.id)}
          />
        </TableCell>
      )}
      {showCheckbox && isChild && <TableCell className="w-[40px]" />}
      <TableCell className="w-[40px]" onClick={(e) => e.stopPropagation()}>
        {!isChild && <StarOrderButton orderId={order.id} size="sm" />}
      </TableCell>
      <TableCell className={cn("whitespace-nowrap", isChild && "bg-muted")}>
        <div className="flex items-center gap-2">
          {isChild && (
            <div className="ml-4">
              <span className="font-medium">Leg {legNumber}</span>
              <div className="text-xs text-muted-foreground">
                of {totalLegs} for {parentUniqueId}
              </div>
            </div>
          )}
          {!isChild && (
            <>
              <span className="font-mono font-medium">{order.unique_id}</span>
              {order.is_recurring && (
                <span title="Recurring order">
                  <Repeat className="h-3 w-3 text-muted-foreground" />
                </span>
              )}
            </>
          )}
        </div>
        <TrailerArrivedBadge legs={legWithArrivedTrailer ? [{ ...legWithArrivedTrailer, leg_sequence: null }] : []} />
      </TableCell>
      <TableCell>
        {order.status === "canceled" ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <OrderStatusBadge status={order.status} />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Reason: {(order as any).cancellation_reason?.trim() || "—"}
            </TooltipContent>
          </Tooltip>
        ) : (
          <OrderStatusBadge
            status={order.status}
            late={lateDep || lateArr}
            legs={isChild ? undefined : order.legs ?? legs}
          />
        )}
      </TableCell>
      <TableCell className="text-sm">
        {order.trip_type || ""}
      </TableCell>
      <TableCell className="text-sm whitespace-nowrap">
        <div className="font-medium">{fromLabel}</div>
        <ReadyTimeDisplay order={order} variant="compact" />
      </TableCell>
      <TableCell className="text-sm whitespace-nowrap">
        <div className="font-medium">{toLabel}</div>
        <DeliveryTimeDisplay order={order} variant="compact" />
      </TableCell>
      <TableCell className="text-sm whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        {!isChild ? (
          <InlineAssignCell
            orderId={order.id}
            kind="tractor"
            currentId={(order as any).assigned_tractor_id ?? null}
            currentName={order.assigned_tractor?.name ?? null}
            readOnly={isTerminal}
          />
        ) : (
          <span className="text-sm">
            {order.assigned_tractor?.name || <span className="text-muted-foreground">—</span>}
          </span>
        )}
      </TableCell>

      <TableCell onClick={(e) => e.stopPropagation()}>
        {!isChild ? (
          <InlineEditCell
            orderId={order.id}
            fieldName="trailer_id"
            currentValue={order.trailer_id}
            placeholder="e.g., TRL-001"
            orderTrailerTypeId={order.trailer_type_id}
            orderShipFromCity={order.ship_from_city}
            readOnly={isTerminal}
          />
        ) : (
          <span className="text-sm">
            {order.trailer_id || <span className="text-muted-foreground">—</span>}
          </span>
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {!isChild ? (
          <InlineTrailerTypeCell orderId={order.id} currentValue={order.trailer_type_id} readOnly={isTerminal} />
        ) : (
          <span className="text-sm capitalize">
            {childTrailerTypeLabel || <span className="text-muted-foreground">—</span>}
          </span>
        )}
      </TableCell>
      {showLoadStatus && (
        <TableCell className="text-sm">
          <OrderLoadStatusCell
            status={order.status}
            liveLoadStatus={loadStatus}
            historical={loadStatusHistorical}
            className="text-sm"
          />
        </TableCell>
      )}
      <TableCell onClick={(e) => e.stopPropagation()}>
        {!isChild && (
          <InlineEditCell
            orderId={order.id}
            fieldName="tender_id"
            currentValue={order.tender_id}
            placeholder="e.g., TND-001"
          />
        )}
      </TableCell>
      <TableCell className="text-sm whitespace-nowrap">
        {!isChild ? order.billing_party?.organization_name : ""}
      </TableCell>
      <TableCell className="text-sm whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        {!isChild ? (
          <InlineAssignCell
            orderId={order.id}
            kind="operator"
            currentId={(order as any).assigned_operator_id ?? null}
            currentName={order.assigned_operator?.name ?? null}
            readOnly={isTerminal}
          />
        ) : (
          <span className="text-sm">
            {[order.assigned_operator?.name, (order as any).assigned_operator_2?.name]
              .filter(Boolean)
              .join(" / ") || <span className="text-muted-foreground">—</span>}
          </span>
        )}
      </TableCell>

      <TableCell>
        {!isChild && (actionsSlot ?? defaultActions)}
      </TableCell>
    </TableRow>
  );
}
